import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReportStatus, UserInfo } from '../types';
import { useFilteredReports } from '../hooks/useFilteredReports';
import { useAgent } from '../hooks/useAgent';
import { ReportFilters } from '../components/ReportFilters/ReportFilters';
import { ReportTable } from '../components/ReportTable/ReportTable';
import { Pagination } from '../components/Pagination';
import { AgentInfo, CurrentUserInfo } from '../components/sidebar';
import { authService } from '../services/authService';
import { FormHeader } from '../components/FormHeader';
import { BreadcrumbItem } from '../components/Breadcrumb';
import { ServiceSwitcher, ServiceSwitcherOption } from '../components/ServiceSwitcher';
import { LanguageSelector } from '../components/LanguageSelector';
import PassportPanel from '../components/PassportPanel/PassportPanel';
import './MainForm.css';

type ServiceMode = 'service' | 'passport';

const MainForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reports, loading, filter, setFilter, resetFilters, page, pageSize, totalPages, totalElements, setPage, setPageSize } =
    useFilteredReports();
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [serviceMode, setServiceMode] = useState<ServiceMode>('service');

  const { agent } = useAgent(currentUser?.agentId);

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
    }
  }, []);

  const handleNewReport = (): void => {
    navigate('/report/new');
  };

  const handleOpenReport = (reportId: string): void => {
    const report = reports.find((r) => r.id === reportId);
    if (report && report.status !== ReportStatus.CANCELED) {
      navigate(`/report/${reportId}`);
    }
  };

  const handleLogout = (): void => {
    authService.logout();
    navigate('/login');
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '', translateKey: 'breadcrumb.dashboard' }
  ];

  const canAccessPassport = currentUser?.passportEnabled === true;
  const canAccessAnalytics = currentUser?.scope === 'GLOBAL' && currentUser?.analyticsEnabled === true;
  const hasExtraServices = canAccessPassport || canAccessAnalytics;
  // T-017: la home Finance (report closed + stato fatturazione) e' visibile anche ad ADMIN e
  // SUPER_ADMIN, non solo al ruolo FINANCE - lo scoping per agente segue le regole gia' esistenti.
  // FINANCE arriva qui da /dashboard e usa lo stesso pulsante per tornare alla propria home.
  const canAccessBilling =
    currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'FINANCE';

  useEffect(() => {
    if (!canAccessPassport && serviceMode === 'passport') {
      setServiceMode('service');
    }
  }, [canAccessPassport, serviceMode]);

  const services = [
    {
      id: 'service' as ServiceMode,
      label: t('serviceSwitcher.serviceReport'),
      logo: '/proservice logo.jpg',
    },
    ...(canAccessPassport
      ? [
          {
            id: 'passport' as ServiceMode,
            label: t('serviceSwitcher.passportService'),
            logo: '/passport logo.jpg',
          },
        ]
      : []),
  ];

  const currentService = services.find((s) => s.id === serviceMode);
  const currentServiceLabel = currentService?.label ?? '';
  const analyticsLabel = t('serviceSwitcher.analyticsService');

  const switcherOptions: ServiceSwitcherOption[] = [
    ...services.map((s) => ({
      id: s.id,
      label: s.label,
      logo: s.logo,
      active: s.id === serviceMode,
      onSelect: () => setServiceMode(s.id),
    })),
    ...(canAccessAnalytics
      ? [
          {
            id: 'analytics',
            label: analyticsLabel,
            logo: '/analytics logo.jpeg',
            active: false,
            onSelect: () => navigate('/analytics'),
          },
        ]
      : []),
  ];

  return (
    <div className="main-form">
      <FormHeader breadcrumbItems={breadcrumbItems}>
        <div className="header-actions">
          {canAccessBilling && (
            <button
              type="button"
              className="btn-primary btn-billing"
              onClick={() => navigate('/billing')}
            >
              <span className="material-icons">receipt_long</span>
              {t('billing.sectionTitle')}
            </button>
          )}
          <ServiceSwitcher
            visible={hasExtraServices}
            currentLabel={hasExtraServices ? currentServiceLabel : 'ProService'}
            currentLogo={hasExtraServices ? currentService?.logo ?? '/proservice logo.jpg' : '/proservice logo.jpg'}
            options={switcherOptions}
          />
          <LanguageSelector />
        </div>
      </FormHeader>

      <div className="main-layout">
        <div className="main-content">
          {serviceMode === 'passport' ? (
            <PassportPanel />
          ) : (
            <>
              <ReportFilters
                filter={filter}
                onFilterChange={setFilter}
                onReset={resetFilters}
                actionButton={
                  <button
                    onClick={handleNewReport}
                    className="btn-primary"
                    aria-label={t('mainForm.newReport')}
                  >
                    <span className="material-icons">add</span> {t('mainForm.newReport')}
                  </button>
                }
              />

              {loading && <div className="loading">{t('app.loading')}</div>}

              {!loading && (
                <>
                  <ReportTable
                    reports={reports}
                    onReportOpen={handleOpenReport}
                  />
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalElements}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* User Sidebar */}
        <aside className="user-sidebar">
          <CurrentUserInfo user={currentUser} agent={agent} onLogout={handleLogout} />
          <AgentInfo agent={agent} />
        </aside>
      </div>
    </div>
  );
};

export default MainForm;
